import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import StatusBadge from "../../components/admin/shared/StatusBadge";
import Paginator from "../../components/admin/shared/Paginator";
import SectionCard from "../../components/admin/shared/SectionCard";
import {
  fetchAdminCompetitions, deleteCompetition, startCompetition,
  AdminCompetitionListItem,
} from "../../api/admin";
import {
  card, primaryBtn, secondaryBtn, dangerBtn, smallBtn,
  inputStyle, errorStyle,
} from "../../styles";

interface Props {
  user: User;
}

const COMP_PAGE_SIZE = 15;

export default function CompetitionsPage({ user }: Props) {
  const { t } = useTranslation();
  const [competitions, setCompetitions] = useState<AdminCompetitionListItem[]>([]);
  const [compTotal, setCompTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const compDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Delete modal
  const [confirmDelete, setConfirmDelete] = useState<AdminCompetitionListItem | null>(null);
  const [deleteWithLeagues, setDeleteWithLeagues] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Search + pagination
  const [compSearch, setCompSearch] = useState("");
  const [compPage, setCompPage] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);

  const load = async (s: string, p: number) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminCompetitions(user, { search: s || undefined, page: p });
      setCompetitions(data.competitions);
      setCompTotal(data.total);
    } catch {
      setError(t("admin.competitions.errors.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("", 1);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (compDebounceRef.current) clearTimeout(compDebounceRef.current);
    compDebounceRef.current = setTimeout(() => { setCompPage(1); load(compSearch, 1); }, 300);
  }, [compSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(compSearch, compPage); }, [compPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteCompetition(user, confirmDelete.id, deleteWithLeagues);
      setConfirmDelete(null);
      setDeleteWithLeagues(false);
      load(compSearch, compPage);
    } catch {
      setError(t("admin.competitions.errors.failedToDelete"));
    } finally {
      setDeleting(false);
    }
  };

  const handleStart = async (compId: string) => {
    setActionLoading(true);
    try {
      await startCompetition(user, compId);
      load(compSearch, compPage);
    } catch {
      setError(t("admin.competitions.errors.failedToStart"));
    } finally {
      setActionLoading(false);
    }
  };

  const compTotalPages = Math.ceil(compTotal / COMP_PAGE_SIZE);

  return (
    <div>
      <SectionCard
        title={t("admin.competitions.title")}
        actions={
          <>
            <input
              style={{ ...inputStyle, width: "200px" }}
              placeholder="Search competitions..."
              value={compSearch}
              onChange={(e) => setCompSearch(e.target.value)}
            />
            <Link to="/admin/competitions/new" style={{ ...primaryBtn, textDecoration: "none", display: "inline-block" }}>
              {t("admin.competitions.create")}
            </Link>
          </>
        }
      >
        {/* Body */}
        <div style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {error && <p style={errorStyle}>{error}</p>}
          {loading ? <p style={{ color: "var(--color-text-muted)" }}>{t("common.loading")}</p> : (
            <>
              {competitions.length === 0 && <p style={{ color: "var(--color-text-muted)" }}>{t("admin.competitions.noCompetitions")}</p>}
              {competitions.map((comp) => (
                <div key={comp.id} style={{ ...card, marginBottom: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <Link
                        to={`/admin/competitions/${comp.id}`}
                        style={{ color: "var(--color-accent)", textDecoration: "none", fontWeight: 600 }}
                      >
                        {comp.name}
                      </Link>
                      <span style={{ marginLeft: "0.5rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                        {comp.season} · {comp.type === "CLUB" ? "Club" : "International"} · {comp.gameweeks.length - 1} GWs
                      </span>
                      <span style={{ marginLeft: "0.5rem" }}><StatusBadge status={comp.status} /></span>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      {comp.status === "UPCOMING" && (
                        <button style={smallBtn} onClick={() => handleStart(comp.id)} disabled={actionLoading}>
                          {t("admin.competitions.actions.start")}
                        </button>
                      )}

                      <button style={dangerBtn} onClick={() => setConfirmDelete(comp)}>
                        {t("admin.competitions.actions.delete")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <Paginator page={compPage} totalPages={compTotalPages} onPageChange={setCompPage} />
            </>
          )}
        </div>
      </SectionCard>

      {/* Delete modal */}
      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} blur={false}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--color-surface)", borderRadius: "20px", border: "1px solid var(--color-border)", padding: "1.5rem", maxWidth: "400px", width: "90%" }}>
            <h3 style={{ marginTop: 0 }}>{t("admin.competitions.deleteModal.title")}</h3>
            <p>{t("admin.competitions.deleteModal.message", { name: confirmDelete.name })}</p>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem", fontSize: "0.9rem" }}>
              <input type="checkbox" checked={deleteWithLeagues} onChange={(e) => setDeleteWithLeagues(e.target.checked)} />
              {t("admin.competitions.deleteModal.deleteLeaguesLabel")}
            </label>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
              <button style={secondaryBtn} onClick={() => setConfirmDelete(null)}>{t("common.cancel")}</button>
              <button style={dangerBtn} onClick={handleDelete} disabled={deleting}>
                {deleting ? t("common.saving") : t("admin.competitions.deleteModal.confirm")}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
